import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SettingsButton } from "@/Common/Components/Settings/SettingsButton";
import { SpanControl } from "@/Common/Components/SpanControl";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { FilePreviewComponentEditable } from "@/ExportableFile/Components/FileSelection/FilePreviewComponentEditable";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FollowUpItem } from "@/Patient/Models/MedicalSummary/FollowUpItem";
import { FollowUpTableItemModel } from "@/Patient/Models/MedicalSummary/FollowUpTableItemModel";
import { FollowUpResponse } from "@/Patient/Models/MedicalSummary/Response/FollowUpResponse";
import { MedicalRequestFactory } from "@/Portfolio/Components/MedicalRequestForm/MedicalRequestFactory";

import img_loading from "@/Globals/_images/loading.gif";

interface IFollowUpSelectionComponentProps {
    patientContactFacetId: string;
    encounterId?: string;

    followupsToHideList?: FollowUpResponse[];

    onClose: () => void;
    onAttached: (followups: FollowUpItem[]) => void;
}

class FollowUpSelectionComponentState {
    public allFollowups: FollowUpTableItemModel[];
    public followupsToDisplay: FollowUpTableItemModel[];
    public isLoading: boolean;

    public isAllItemChecked: boolean;
    public showSetting: boolean;
    public searchText: string;
    public dateFrom: Date;
    public dateTo: Date;

    public showFilePreview: boolean;
    public isFilePreviewLoaded: boolean;

    public selectedFile: FollowUpTableItemModel;
    public selectedFilePrintable: ExportableFile;
}

class FollowupSelectionComponent extends BaseReactComponent<IFollowUpSelectionComponentProps, FollowUpSelectionComponentState> {
    private saveOptionKey: string = "FollowupMain";

    public componentWillMount(): void {
        let state = new FollowUpSelectionComponentState();
        state.allFollowups = [];
        state.followupsToDisplay = [];
        this.setState(state);

        sessionStore.showGlobalOverlay(true);
    }

    public componentDidMount(): void {
        this.load();
    }

    public componentWillUnmount(): void {
        sessionStore.hideGlobalOverlay();
    }

    public render(): JSX.Element {
        if (!this.props.patientContactFacetId)
            return null;

        const tableColumnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getFollowUpMyleTableColumnsDefaultOptions());

        const leftSideWidth = this.state.showFilePreview == true ? "40%" : "100%";
        const rightSideWidth = this.state.showFilePreview == true ? "60%" : "100%";

        return (
            <div
                id={`followup_selection_${this.props.patientContactFacetId}`}
                className="filePreview-popup popup-attachment-main popup-with-grid"
            >
                <div className="dam-header p-header">
                    <div className="flex-container">
                        <div>
                            <h2>{cacheStore.GetString("followUpLabel")}</h2>
                        </div>

                        <div className="flex-container popup-attachment-header">
                            <div className="popup-attachment-header-patientInfo" />
                            <div className="flex-container popup-attachment-header-right">
                                <div className="popup-attachment-header-right-CloseWrapper">
                                    <div className="pointer popup-attachment-header-right-btnClose" onClick={() => this.props.onClose()}>X</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dam-main">
                    <div className="flex-container justifyContent-start">
                        {this.renderFileList(leftSideWidth, tableColumnsOptions)}
                        {this.renderSeparator()}
                        {this.renderFilePreview(rightSideWidth)}
                    </div>
                </div>

                <div className="dam-footer">
                    <div className="row">
                        <div className="col-lg-9 col-md-9 col-sm-9">
                            {this.state.isLoading && <img src={img_loading} alt="loading" />}
                        </div>
                        <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                            <div className="flex-container justifyContent-end">
                                {this.renderFooterControls()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderSeparator(): JSX.Element {
        return (
            <div
                className="flex-container aside-handle"
                style={{ "justifyContent": "center", "flexDirection": "column", "display": "flex" }}
                onClick={() => this.mutateState(s => s.showFilePreview = !this.state.showFilePreview)}
            >
                <div className="slide-menu-icon blue">
                    <span /><span /><span />
                </div>
            </div>
        );
    }

    private renderFilePreview(rightSideWidth: string): JSX.Element {
        if (!this.state.showFilePreview || !this.state.selectedFilePrintable)
            return null;

        return (
            <div className="flex-container" style={{ "width": rightSideWidth, "marginLeft": "20px", "flexDirection": "column" }}>
                <div>
                    <div className="flex-container">
                        <div className="flex-container" style={{ "flexDirection": "column", "justifyContent": "flex-start" }}>
                            <div />
                        </div>
                    </div>
                </div>

                <div className="popup-attachment-right-body">
                    <FilePreviewComponentEditable
                        file={this.state.selectedFilePrintable}
                        onFilePreviewLoaded={() => this.mutateState(s => s.isFilePreviewLoaded = true)}
                    />
                </div>
            </div>
        );
    }

    private renderFileList(leftSideWitdh: string, tableColumnsOptions: Immutable.List<MyleTableColumnOptions>): JSX.Element {
        return (
            <div style={{ width: leftSideWitdh }}>
                <div className="popup-attachment-left-head">
                    <div className="pull-right">
                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                            <div>
                                <TextboxControl
                                    debounceMs={400}
                                    value={this.state.searchText}
                                    onChange={val => this.handleSearchTextChanged(val)}
                                    className="form-control popup-attachment-left-txtbox"
                                    placeholder={cacheStore.GetString("SearchLabel")}
                                />
                            </div>
                            <div>
                                <RefreshButton rightMargin={5} onClick={() => this.load()} />
                            </div>
                            <div>
                                <SettingsButton rightMargin={13} onClick={() => this.mutateState(s => s.showSetting = true)} />
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showSetting &&
                    this.renderSetting()
                }

                <div className="fxheightpopup flex-container">
                    <MyleTable
                        TableRenderOptions={MyleTable.LoadDefaultTableRenderOptions(tableColumnsOptions)}
                        Columns={this.getFollowUpTableColumns(tableColumnsOptions)}
                        TableId="filePreview_table"
                        DataSource={Immutable.List(this.state.followupsToDisplay)}
                        RowClickOperation={null}
                        RowClickFunction={item => this.handleOnRowClick(item)}
                        SaveOptionskeyTarget={this.saveOptionKey}
                    />
                </div>

            </div>
        );
    }

    private renderSetting(): JSX.Element {
        const id = "followup-setting";
        const settingsPopupPosition = this.state.showFilePreview == true ? "50%" : "0%";

        return (
            <div onClick={() => this.mutateState(s => s.showSetting = false, this.load)} className="modal-cover-screen">
                <div id={id} className="filter-component popup-attachment-settingsPopup" style={{ top: "100px", right: "50px", marginRight: settingsPopupPosition }} onClick={e => e.stopPropagation()}>
                    <table className="f-content popup-settings-table">
                        <thead>
                            <tr>
                                <th>
                                    <label className=".popup-settings-lblDate">{cacheStore.GetString("DateLabel")}</label>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div className="popup-settings-dateSelector">
                                        <DateSelector id="dateFromSelector_Id" dateFormat="yyyy-MM-dd" date={this.state.dateFrom} onChangeCallback={date => this.mutateState(s => s.dateFrom = date)} toDayEnabled={true} />
                                    </div>
                                    <div className="popup-settings-dateSelector">
                                        <DateSelector id="dateToSelector_Id" dateFormat="yyyy-MM-dd" date={this.state.dateTo} onChangeCallback={date => this.mutateState(s => s.dateTo = date)} toDayEnabled={true} />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="f-footer clearfix">
                        <div className="pull-right">
                            <ButtonControl className="btn btn-default popup-settings-btnCancel apss-settings-btn" onClick={() => this.mutateState(s => s.showSetting = false)} label={cacheStore.GetString("CancelLabel").toUpperCase()} />
                            <ButtonControl className="btn btn-primary popup-settings-btnOk apss-settings-btn" onClick={() => this.mutateState(s => s.showSetting = false, this.load)} label={cacheStore.GetString("OKLabel").toUpperCase()} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderFooterControls(): JSX.Element {
        let selectedFiles = this.getCheckedFiles();
        return (
            <div>
                <button className="btn btn-default" onClick={() => this.props.onClose()}>{cacheStore.GetString("CloseLabel")}</button>
                <button
                    key="attach"
                    className="btn btn-primary popup-attachment-footer-btn"
                    disabled={selectedFiles.length === 0}
                    onClick={() => this.handleOnAttached(selectedFiles)}
                >
                    {cacheStore.GetString("AttachLabel")}
                </button>
            </div>
        );
    }

    private load(): void {
        if (!this.props.patientContactFacetId)
            return;

        const requestData = {
            PatientContactFacetId: this.props.patientContactFacetId,
            EncounterId: this.props.encounterId,
            LoadNonLinkedOnly: true,
            DateFrom: this.state.dateFrom,
            DateTo: this.state.dateTo ? moment(this.state.dateTo).add("days", 1) : null
        };

        this.mutateState(s => s.isLoading = true);

        Client.Postv2(
            ServerOperations.Server.Myle_LoadFollowUps,
            requestData,
            response => {
                if (response.FollowUps) {
                    let followupsItem = response.FollowUps.map(x => new FollowUpItem(x));
                    this.mutateState(s => {
                        s.isLoading = false;
                        s.allFollowups = followupsItem.map(x => new FollowUpTableItemModel(x));
                        s.followupsToDisplay = this.buildFollowupsToDisplay(s.allFollowups);
                    });
                }
            },
            () => this.mutateState(s => s.isLoading = false)
        );
    }

    private handleOnRowClick(item: FollowUpTableItemModel): void {
        this.mutateState(
            s => s.selectedFile = item,
            () => this.loadFilePreview(item.Model.model.Id, item.Model.getFollowUpType())
        );
    }

    private loadFilePreview(id: string, type: MedicalRequestType): void {
        if (!id)
            return;

        this.mutateState(s => s.isLoading = true);

        let action = MedicalRequestFactory.createAction(type);
        action.generatePDF(
            id,
            false,
            true,
            null,
            file => this.mutateState(s => {
                s.selectedFilePrintable = file;
                s.showFilePreview = true;
                s.isLoading = false;
            }),
            () => this.mutateState(s => s.isLoading = false)
        );
    }

    private handleSearchTextChanged(text: string): void {
        this.mutateState(s => {
            s.searchText = text;
            s.followupsToDisplay = this.buildFollowupsToDisplay(s.allFollowups, s.searchText);
        });
    }

    private buildFollowupsToDisplay(followups: FollowUpTableItemModel[], searchText?: string): FollowUpTableItemModel[] {
        if (searchText) {
            followups = followups.filterSearchAdvanced(
                searchText,
                (IsMatchingSearchTextFct, item: FollowUpTableItemModel) =>
                    IsMatchingSearchTextFct(item.Name)
                    || IsMatchingSearchTextFct(item.Model.model.Reason)
                    || IsMatchingSearchTextFct(item.Model.getCreationDateAsDigit())
            );

            followups = followups.sortByProperty(x => x.Model.model.CreationDate);
        }

        if (!isArrayNullOrUndefinedOrEmpty(this.props.followupsToHideList))
            followups = followups.filter(x => !this.props.followupsToHideList.some(y => y.Id === x.Model.model.Id && y.MedicalRequestType === x.Model.getFollowUpType()));

        return followups;
    }

    private handleOnAttached(selected: FollowUpTableItemModel[]): void {
        if (this.props.onAttached) {
            this.props.onAttached(selected.map(x => x.Model));
        }
    }

    private getCheckedFiles(): FollowUpTableItemModel[] {
        let checkedFiles = this.state.followupsToDisplay.filter(x => x.IsChecked);
        if (checkedFiles.length === 0 && this.state.selectedFile)
            checkedFiles = [this.state.selectedFile];
        return checkedFiles;
    }

    private getFollowUpTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const dateFormat = "YYYY/MM/DD";

        return MyleTableColumn.buildList<FollowUpTableItemModel>(columnsOptions, column => {
            switch (column.FieldName) {
                case "Checked":
                    return {
                        renderHeader: () => (
                            <div>
                                <CheckboxControl
                                    id="FilesAllCheckedPopup"
                                    checked={this.state.isAllItemChecked}
                                    onChange={() =>
                                        this.mutateState(s => {
                                            s.isAllItemChecked = !s.isAllItemChecked;
                                            s.followupsToDisplay = s.followupsToDisplay.mutate(x => x.IsChecked = s.isAllItemChecked);
                                        })
                                    }
                                />
                            </div>
                        ),
                        renderCell: item => (
                            <div>
                                <CheckboxControl
                                    id={item.Model.model.Id}
                                    checked={item.IsChecked}
                                    onChange={() => this.mutateState(s => {
                                        s.followupsToDisplay = s.followupsToDisplay.mutate(x => {
                                            if (x.itemId === item.itemId)
                                                x.IsChecked = !item.IsChecked;
                                        });
                                    })}
                                />
                            </div>
                        )
                    };

                case "Item":
                    return {
                        renderCell: item => {
                            let followUpItem = new FollowUpItem(item.Model.model);
                            let followUpName = followUpItem.getSpeciality();

                            return (
                                <div><SpanControl title={followUpName} text={followUpName} /></div>
                            );
                        }
                    };

                case "Reason":
                    return {
                        renderCell: item => {
                            let reason: string = item.Model.model.Reason ? item.Model.model.Reason : "";

                            return (
                                <div><SpanControl text={reason} /></div>
                            );
                        }
                    };

                case "CreationDate":
                    return {
                        renderCell: item => {
                            let creationDate: string = item.Model.model.CreationDate ? moment(item.Model.model.CreationDate).format(dateFormat) : "";

                            return (
                                <div><SpanControl text={creationDate} /></div>
                            );
                        }
                    };

                case "Priority":
                    return {
                        renderCell: item => {
                            let followUpItem = new FollowUpItem(item.Model.model);
                            let priorityName = followUpItem.getPriority().getName();

                            let urgentStyle: React.CSSProperties = {};
                            if (followUpItem.isUrgent()) {
                                urgentStyle = { color: "red" };
                            }

                            return (<div><SpanControl style={urgentStyle} text={priorityName} /></div>);
                        }
                    };

                case "Status":
                    return {
                        renderCell: item => {
                            let status = cacheStore.GetLocalizedValueByLookupId(item.Model.model.StatusLookupId);

                            return (<div><SpanControl title={status} text={status} /></div>);
                        }
                    };
            }
        });
    }

    private getFollowUpMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        let cols: MyleTableColumnOptions[] = [];

        let checkColumn = new MyleTableColumnOptions();
        checkColumn.HeaderText = "";
        checkColumn.FieldName = "Checked";
        checkColumn.IsFixedInPlace = true;
        checkColumn.RowClickEnabled = false;
        checkColumn.IsSortable = false;
        checkColumn.WidthPixels = 75;

        let itemColumn = new MyleTableColumnOptions();
        itemColumn.HeaderText = cacheStore.GetString("ItemLabel");
        itemColumn.FieldName = "Item";
        itemColumn.RowClickEnabled = true;
        itemColumn.IsSortable = false;
        itemColumn.WidthPercentageDefault = 20;

        let reasonColumn = new MyleTableColumnOptions();
        reasonColumn.HeaderText = cacheStore.GetString("ReasonLabel");
        reasonColumn.FieldName = "Reason";
        reasonColumn.RowClickEnabled = true;
        reasonColumn.IsSortable = false;
        reasonColumn.WidthPercentageDefault = 20;

        let priorityColumn = new MyleTableColumnOptions();
        priorityColumn.HeaderText = cacheStore.GetString("PriorityLabel");
        priorityColumn.FieldName = "Priority";
        priorityColumn.RowClickEnabled = true;
        priorityColumn.IsSortable = false;
        priorityColumn.WidthPercentageDefault = 20;

        let creationDateColumn = new MyleTableColumnOptions();
        creationDateColumn.HeaderText = cacheStore.GetString("CreationDateLabel");
        creationDateColumn.FieldName = "CreationDate";
        creationDateColumn.RowClickEnabled = true;
        creationDateColumn.IsSortable = false;
        creationDateColumn.WidthPercentageDefault = 15;

        let statusColumn = new MyleTableColumnOptions();
        statusColumn.HeaderText = cacheStore.GetString("StatusLabel");
        statusColumn.FieldName = "Status";
        statusColumn.RowClickEnabled = true;
        statusColumn.IsSortable = false;
        statusColumn.WidthPercentageDefault = 15;

        cols.push(checkColumn);
        cols.push(itemColumn);
        cols.push(reasonColumn);
        cols.push(priorityColumn);
        cols.push(creationDateColumn);
        cols.push(statusColumn);

        return cols;
    }
}

export { FollowupSelectionComponent };
